import React, { useState } from "react";
import { Badge, Divider, Box } from "@mui/material";
import AlarmDetail from "./AlarmDetail";
import PropTypes from "prop-types";
import { groupAlarmsBySeverity } from "../../../lib/AlarmUtil";
import {
  ALARM_SEVERITIES,
  getAlarmSeverityColor,
  normalizeAlarmSeverity,
} from "../../../lib/AlarmColors";
import Popover from "@mui/material/Popover";
import { useTheme } from "@mui/material/styles";

const AlarmBadge = ({
  alarms,
  alarmsCount,
  children,
  timezone,
  badgeProps,
  source,
  hasAssets = true,
  onShowAlarmModal,
  needsAssetNames,
}) => {
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState(null);

  if (alarms?.length === 0) return <></>;

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const getBadgeCount = (alarms) => {
    if (!alarms) return 0;
    return alarms.length;
  };

  // The badge takes the color of the worst severity actually counted.
  const getBadgeSeverity = (alarmsCount) =>
    ALARM_SEVERITIES.find((severity) =>
      alarmsCount?.some(
        (count) => normalizeAlarmSeverity(count.severity) === severity && count.count > 0,
      ),
    );

  const showAlarmModal = (event) => {
    event.stopPropagation();
    onShowAlarmModal(alarms);
  };

  const badgeSeverity = getBadgeSeverity(alarmsCount);
  const badgeColor = badgeSeverity ? getAlarmSeverityColor(badgeSeverity) : theme.palette.grey.main;

  const styles = {
    popoverBox: {
      ...theme.classes.customTooltipContainer,
      ...theme.classes.alarmDetailContainer,
    },
    badge: {
      cursor: "pointer",
      "& .MuiBadge-badge": {
        backgroundColor: badgeColor,
        color: theme.palette.getContrastText(badgeColor),
        "&:hover": {
          filter: "drop-shadow(0px 4px 3px rgba(0, 0, 0, 0.35))",
        },
      },
    },
  };

  const displayAlarms = (alarms) =>
    alarms.map((alarm) => (
      <Box key={alarm.name}>
        <AlarmDetail
          alarmSeverity={alarm.alarm_type}
          alarmDate={alarm.date}
          alarmName={alarm.name}
          alarmSource={alarm.source}
          alarmAssetName={alarm.type}
          timezone={timezone}
          resolved={false}
          needsAssetNames={needsAssetNames}
        />
      </Box>
    ));

  const getBadgeContent = (alarms) => {
    const count = getBadgeCount(alarms);
    return (
      <>
        <span>{count}</span>
      </>
    );
  };

  return (
    <>
      <Badge
        badgeContent={getBadgeContent(alarms)}
        sx={styles.badge}
        {...badgeProps}
        aria-owns={open ? "mouse-over-popover" : undefined}
        aria-haspopup="true"
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        onClick={showAlarmModal}
      />

      <Popover
        id="mouse-over-popover"
        sx={{
          pointerEvents: "none",
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
        transformOrigin={{ vertical: -5, horizontal: -5 }}
      >
        <Box sx={styles.popoverBox}>
          {groupAlarmsBySeverity(alarms).map((group, index) => (
            <React.Fragment key={group.severity}>
              {index > 0 && (
                <Box sx={{ mt: ".8rem", mb: "1rem" }}>
                  <Divider sx={{ mx: "1rem", borderBottomWidth: 2 }} />
                </Box>
              )}
              {displayAlarms(group.alarms)}
            </React.Fragment>
          ))}
        </Box>
      </Popover>
    </>
  );
};

AlarmBadge.propTypes = {
  alarms: PropTypes.array,
  alarmsCount: PropTypes.array,
  timezone: PropTypes.string,
  children: PropTypes.object,
  badgeProps: PropTypes.object,
  source: PropTypes.string,
  hasAssets: PropTypes.bool,
  onShowAlarmModal: PropTypes.func.isRequired,
  needsAssetNames: PropTypes.bool,
};

export default AlarmBadge;
